/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useWallet } from '../providers/WalletProvider';
import { useAiContext } from '../providers/ai-provider';
import { useMarketData } from '../hooks/use-market-data';
import { 
    Search, LayoutGrid, List as ListIcon, Loader2, Flame, ArrowRight,
    Info, Star, ArrowUp, ArrowDown, DollarSign, BarChart2, PieChart, Activity, ChevronLeft, ChevronRight,
    Brain, Shield, ShieldAlert, Users, Zap, ArrowLeftRight, Globe
} from 'lucide-react';
import { TradeModal } from './TradeModal';
import { TokenDetailsModal } from './TokenDetailsModal';
import { MarketToken } from '../data/market-data';
import { bondingCurveDB } from '../lib/bonding-curve-db';
import { formatCurrency, formatCompact, formatAddress, formatPercentage } from '../lib/utils/format';
import { useWatchlist } from '../context/WatchlistContext';
import { GlobalMarketData } from '../lib/coinmarketcap';
import { TokenMetadata } from '../lib/pinata';

interface AllMarketsProps {
    onBack: () => void;
}

type ViewMode = 'grid' | 'list';
type ChainFilter = 'ALL' | 'SOL' | 'SUI';
type CategoryFilter = 'ALL' | 'WATCHLIST' | 'AI_SIGNALS' | 'SMART_MONEY';
type SortKey = 'marketCap' | 'price' | 'change24h' | 'smartMoneyCount' | 'riskScore';
type SortDirection = 'asc' | 'desc';

// Extended type for internal use
interface EnhancedMarketToken extends MarketToken {
    aiSignal?: 'BUY' | 'SELL';
    aiConfidence?: number;
    smartMoneyCount?: number;
    riskScore?: number; // 0 (Low) - 100 (Critical)
    volumeTrend?: 'HIGH' | 'NORMAL' | 'LOW';
}

// --- Memoized Sub-Components for Optimization ---

const Sparkline: React.FC<{ data: number[]; isUp: boolean }> = React.memo(({ data, isUp }) => {
    if (!data || data.length < 2) return <div className="w-24 h-8" />;
    
    const width = 100;
    const height = 30;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min === 0 ? 1 : max - min;

    const points = data.map((d, i) => {
        const x = (i / (data.length - 1)) * width;
        const y = height - ((d - min) / range) * height;
        return `${x},${y}`;
    }).join(' ');

    const color = isUp ? '#16a34a' : '#dc2626';

    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-24 h-8" preserveAspectRatio="none">
            <path d={`M ${points}`} fill="none" stroke={color} strokeWidth="2" />
        </svg>
    );
});

const MarketHeader: React.FC<{ stats: GlobalMarketData | null }> = React.memo(({ stats }) => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm flex flex-col items-center justify-center text-center h-32 hover:shadow-md transition-shadow group">
             <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                <DollarSign size={12}/> Market Cap
             </h4>
             <div className="font-serif text-3xl text-stone-900 tracking-tight group-hover:text-nobel-gold transition-colors">
                {stats ? `$${formatCompact(stats.total_market_cap.usd)}` : <Loader2 size={24} className="animate-spin text-stone-300"/>}
             </div>
        </div>
        
        <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm flex flex-col items-center justify-center text-center h-32 hover:shadow-md transition-shadow group">
             <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                <BarChart2 size={12}/> 24h Volume
             </h4>
             <div className="font-serif text-3xl text-stone-900 tracking-tight group-hover:text-nobel-gold transition-colors">
                 {stats ? `$${formatCompact(stats.total_volume.usd)}` : <Loader2 size={24} className="animate-spin text-stone-300"/>}
             </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm flex flex-col items-center justify-center text-center h-32 hover:shadow-md transition-shadow group">
             <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                <PieChart size={12}/> BTC Dominance
             </h4>
             <div className="font-serif text-3xl text-stone-900 tracking-tight group-hover:text-nobel-gold transition-colors">
                {stats ? `${stats.market_cap_percentage.btc.toFixed(1)}%` : <Loader2 size={24} className="animate-spin text-stone-300"/>}
             </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm flex flex-col items-center justify-center text-center h-32 hover:shadow-md transition-shadow group">
             <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                <Activity size={12}/> Active Markets
             </h4>
             <div className="font-serif text-3xl text-stone-900 tracking-tight group-hover:text-nobel-gold transition-colors">
                 {stats ? stats.markets.toLocaleString().replace(/,/g, ' ') : <Loader2 size={24} className="animate-spin text-stone-300"/>}
             </div>
        </div>
    </div>
));

// Optimized List Row Component
const MarketListRow: React.FC<{ 
    token: EnhancedMarketToken; 
    idx: number; 
    onDetails: (t: EnhancedMarketToken) => void;
    onTrade: (t: EnhancedMarketToken) => void;
}> = React.memo(({ token, idx, onDetails, onTrade }) => {
    const isUp = token.change24h >= 0;
    const { isFavorited, addToWatchlist, removeFromWatchlist } = useWatchlist();
    const mintAddress = token.properties?.mintAddress;
    const isFavorite = mintAddress ? isFavorited(mintAddress) : false;

    const handleFavorite = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isFavorite) removeFromWatchlist(mintAddress || '');
        else addToWatchlist(mintAddress || '');
    };

    return (
        <div 
            className="grid grid-cols-10 lg:grid-cols-12 gap-4 items-center p-4 bg-white rounded-xl border border-stone-200 hover:border-stone-300 hover:shadow-md transition-all cursor-pointer group" 
            onClick={() => onDetails(token)}
            role="button"
            tabIndex={0}
        >
            <div className="col-span-4 lg:col-span-3 flex items-center gap-4 min-w-0">
                <span className="font-mono text-sm text-stone-400 w-6 text-center hidden sm:block">#{idx + 1}</span>
                <div className="w-10 h-10 rounded-lg bg-stone-100 p-0.5 border border-stone-200 flex-shrink-0">
                    <img src={token.image} className="w-full h-full rounded-md object-cover" alt="" loading="lazy" />
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                         <div className="font-bold text-stone-900 truncate group-hover:text-nobel-gold transition-colors">{token.name}</div>
                         <button 
                            onClick={handleFavorite} 
                            className="p-1 text-stone-300 hover:text-amber-500 rounded-full" 
                        >
                            <Star size={14} className={`transition-all ${isFavorite ? 'fill-amber-500 text-amber-500' : 'fill-transparent stroke-current'}`} />
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                         <div className="font-mono text-xs text-stone-500 font-bold">${token.symbol}</div>
                         {token.aiSignal && (
                             <span className={`text-[9px] px-1.5 rounded font-bold uppercase ${token.aiSignal === 'BUY' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                 AI {token.aiSignal}
                             </span>
                         )}
                    </div>
                </div>
            </div>

            <div className="col-span-3 lg:col-span-2 text-right font-mono font-bold text-stone-900">{formatCurrency(token.price, 'USD', token.price < 0.01 ? 6 : 4)}</div>
            
            <div className={`col-span-2 text-right font-mono font-bold ${isUp ? 'text-green-600' : 'text-red-600'}`}>
                {formatPercentage(token.change24h)}
            </div>
            
            <div className="hidden lg:block lg:col-span-2 text-right">
                <div className="flex justify-end gap-1">
                    {(token.smartMoneyCount || 0) > 0 && (
                        <span className="flex items-center gap-1 text-[10px] font-bold bg-blue-50 text-blue-600 px-2 py-1 rounded border border-blue-100" title="Smart Money Activity">
                            <Users size={10} /> {token.smartMoneyCount}
                        </span>
                    )}
                    {(token.riskScore || 0) < 30 && (
                        <span className="flex items-center gap-1 text-[10px] font-bold bg-green-50 text-green-600 px-2 py-1 rounded border border-green-100" title="Low Risk">
                            <Shield size={10} /> Safe
                        </span>
                    )}
                    {(token.riskScore || 0) > 80 && (
                        <span className="flex items-center gap-1 text-[10px] font-bold bg-red-50 text-red-600 px-2 py-1 rounded border border-red-100" title="High Risk">
                            <ShieldAlert size={10} /> Risky
                        </span>
                    )}
                </div>
            </div>

            <div className="hidden lg:col-span-2 lg:flex justify-end">
                <Sparkline data={token.priceHistory || []} isUp={isUp} />
            </div>
            
            {/* Optimized Actions Column */}
            <div className="col-span-1 flex justify-end gap-2">
                <button 
                    onClick={(e) => { e.stopPropagation(); onDetails(token); }} 
                    className="p-2 rounded-lg bg-white border border-stone-200 text-stone-400 hover:text-stone-900 hover:border-stone-300 transition-colors shadow-sm hidden sm:flex" 
                    title="Details"
                >
                    <Info size={16} />
                </button>
                <button 
                    onClick={(e) => { e.stopPropagation(); onTrade(token); }} 
                    className="p-2 rounded-lg bg-stone-900 text-white hover:bg-nobel-gold hover:text-stone-900 transition-colors shadow-md flex items-center gap-2" 
                    title={`Swap ${token.symbol}`}
                >
                    <ArrowLeftRight size={16} />
                    <span className="text-xs font-bold hidden xl:block">Swap</span>
                </button>
            </div>
        </div>
    );
});

// Optimized Grid Card Component
const MarketGridCard: React.FC<{
    token: EnhancedMarketToken;
    idx: number;
    onDetails: (t: EnhancedMarketToken) => void;
    onTrade: (t: EnhancedMarketToken) => void;
}> = React.memo(({ token, onDetails, onTrade }) => {
    const { isFavorited, addToWatchlist, removeFromWatchlist } = useWatchlist();
    const mintAddress = token.properties?.mintAddress;
    const isFavorite = mintAddress ? isFavorited(mintAddress) : false;

    const getProgress = (t: MarketToken) => {
        const curve = t.properties?.mintAddress ? bondingCurveDB.getCurve(t.properties.mintAddress) : null;
        if (curve) return curve.progress;
        return ((t.marketCap / 20000) * 100);
    };

    const progress = getProgress(token);
    const isGraduated = progress >= 100;

    const handleFavorite = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isFavorite) removeFromWatchlist(mintAddress || '');
        else addToWatchlist(mintAddress || '');
    };

    return (
        <div className="bg-white rounded-2xl border border-stone-200 transition-all duration-300 group flex flex-col h-full relative overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-stone-300">
            {token.aiSignal && (
                <div className={`absolute top-0 right-0 px-3 py-1 text-[9px] font-bold text-white rounded-bl-xl z-20 ${token.aiSignal === 'BUY' ? 'bg-green-500' : 'bg-red-500'}`}>
                    AI {token.aiSignal} ({token.aiConfidence}%)
                </div>
            )}
            
            <div className="h-24 w-full bg-stone-100 relative shrink-0">
                {token.header_image ? (
                    <img src={token.header_image} alt="" className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-stone-50 to-stone-100"></div>
                )}
            </div>

            <div className="p-5 flex flex-col flex-1 -mt-8">
                <div className="flex items-start justify-between mb-4">
                     <div className="w-14 h-14 rounded-xl bg-stone-100 p-0.5 border-4 border-white flex-shrink-0 relative overflow-hidden group-hover:scale-105 transition-transform duration-300 shadow-md">
                         <img src={token.image} alt={token.symbol} className="w-full h-full object-cover rounded-lg" loading="lazy" />
                     </div>
                     <div className="flex items-center gap-1">
                         {(token.smartMoneyCount || 0) > 0 && (
                             <div className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100" title="Smart Money Active">
                                 <Users size={12} />
                             </div>
                         )}
                         {(token.riskScore || 0) > 80 && (
                             <div className="w-6 h-6 rounded-full bg-red-50 text-red-600 flex items-center justify-center border border-red-100" title="High Risk">
                                 <ShieldAlert size={12} />
                             </div>
                         )}
                     </div>
                </div>
                
                <div className="mb-2">
                    <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <h3 className="font-serif font-bold text-stone-900 leading-tight truncate text-lg group-hover:text-nobel-gold transition-colors">{token.name}</h3>
                            <button 
                                onClick={handleFavorite}
                                className="p-1 text-stone-300 hover:text-amber-500 transition-colors shrink-0 rounded-full" 
                            >
                                <Star size={16} className={`transition-all ${isFavorite ? 'fill-amber-500 text-amber-500' : 'fill-transparent stroke-current'}`} />
                            </button>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] font-bold bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded-md uppercase">${token.symbol}</span>
                        <span className="font-mono text-[10px] text-stone-400">{formatAddress(token.id, 4)}</span>
                        {isGraduated && (
                            <span className="flex items-center gap-1 bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 text-stone-900 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide border border-amber-300/50 shadow-sm whitespace-nowrap">
                                <Flame size={10} className="text-stone-900 fill-current" /> HOT
                            </span>
                        )}
                    </div>
                </div>
                
                <div className="mt-auto space-y-4">
                    <div className="flex justify-between items-baseline pt-4 border-t border-stone-100">
                         <div className="font-mono text-2xl font-bold text-stone-900">{formatCurrency(token.price, 'USD', token.price < 0.01 ? 6 : 3)}</div>
                         <div className={`font-bold text-xs px-2 py-1 rounded-md ${token.change24h >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                            {formatPercentage(token.change24h)}
                         </div>
                    </div>
                </div>

                <div className="mt-4">
                    <div className="flex justify-between text-[10px] font-bold text-stone-400 mb-1.5 uppercase tracking-wide">
                        <span>Mkt Cap: {token.marketCap ? '$' + formatCompact(token.marketCap) : '-'}</span>
                        <span>Curve: {progress.toFixed(0)}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-700 ${isGraduated ? 'bg-gradient-to-r from-amber-300 to-amber-500' : 'bg-gradient-to-r from-stone-300 to-stone-400'}`} style={{ width: `${Math.min(progress, 100)}%` }}></div>
                    </div>
                </div>
            </div>
            
             {/* Floating Action Buttons */}
             <div className="px-5 pb-5 pt-2 grid grid-cols-2 gap-3">
                <button onClick={() => onDetails(token)} className="py-2.5 bg-stone-50 text-stone-600 font-bold text-xs hover:bg-stone-100 transition-all rounded-xl flex items-center justify-center gap-2 group/btn border border-stone-200" aria-label={`View details for ${token.name}`}>
                    <Info size={14} /> Details
                </button>
                <button onClick={() => onTrade(token)} className="py-2.5 bg-stone-900 text-white font-bold text-xs hover:bg-nobel-gold hover:text-stone-900 transition-all rounded-xl flex items-center justify-center gap-2 group/btn shadow-md" aria-label={`Trade ${token.name}`}>
                    <ArrowLeftRight size={14} /> Swap
                </button>
             </div>
        </div>
    );
});

export const AllMarkets: React.FC<AllMarketsProps> = ({ onBack }) => {
    const { chain } = useWallet();
    const { watchlist } = useWatchlist();
    const { signals, smartMoney } = useAiContext();
    
    // New Hook for Data
    const { tokens, stats: globalStats, isLoading: loading } = useMarketData();
    
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    
    // Split filters
    const [selectedChain, setSelectedChain] = useState<ChainFilter>((chain as ChainFilter) || 'ALL');
    const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('ALL');
    
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<SortKey>('marketCap');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const [tradeToken, setTradeToken] = useState<TokenMetadata | null>(null);
    const [tradePrice, setTradePrice] = useState<number>(0);
    const [detailsToken, setDetailsToken] = useState<EnhancedMarketToken | null>(null);

    // Sync Chain filter with wallet on mount/change if needed, but allow manual override
    useEffect(() => {
        if (chain) setSelectedChain(chain);
    }, [chain]);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 12;

    const enhancedTokens: EnhancedMarketToken[] = useMemo(() => {
        return tokens.map(t => {
            // Check AI Signals
            const signal = signals.find(s => s.token.properties?.mintAddress === t.properties?.mintAddress);
            
            // Check Smart Money Activity
            const smartMoves = smartMoney.filter(trader => trader.lastTrade.tokenMint === t.properties?.mintAddress);
            
            // Simulate Risk based on static data (preserved from original component logic)
            const isNew = (Date.now() - t.launchDate) < 86400000;
            const riskScore = isNew ? 60 + Math.floor(Math.random() * 30) : 20 + Math.floor(Math.random() * 20);

            return {
                ...t,
                aiSignal: signal ? signal.type : undefined,
                aiConfidence: signal ? signal.confidence : undefined,
                smartMoneyCount: smartMoves.length,
                riskScore,
                volumeTrend: t.volume24h > 1000000 ? 'HIGH' : t.volume24h > 100000 ? 'NORMAL' : 'LOW'
            };
        });
    }, [tokens, signals, smartMoney]);

    const sortedAndFilteredTokens = useMemo(() => {
        setCurrentPage(1); // Reset page on filter change
        const filtered = enhancedTokens.filter(t => {
            const matchesSearch = (t.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || (t.symbol?.toLowerCase() || '').includes(searchTerm.toLowerCase());
            
            // Chain Filter Logic
            const matchesChain = selectedChain === 'ALL' || t.properties?.chain === selectedChain;

            // Category Filter Logic
            let matchesCategory = true;
            if (selectedCategory === 'WATCHLIST') {
                matchesCategory = !!(t.properties?.mintAddress && watchlist.includes(t.properties.mintAddress));
            } else if (selectedCategory === 'AI_SIGNALS') {
                matchesCategory = !!t.aiSignal;
            } else if (selectedCategory === 'SMART_MONEY') {
                matchesCategory = (t.smartMoneyCount || 0) > 0;
            }
            
            return matchesSearch && matchesChain && matchesCategory;
        });

        return filtered.sort((a, b) => {
            // Custom sort for risk/smartMoney
            if (sortBy === 'smartMoneyCount') return sortDirection === 'asc' ? (a.smartMoneyCount || 0) - (b.smartMoneyCount || 0) : (b.smartMoneyCount || 0) - (a.smartMoneyCount || 0);
            if (sortBy === 'riskScore') return sortDirection === 'asc' ? (a.riskScore || 0) - (b.riskScore || 0) : (b.riskScore || 0) - (a.riskScore || 0);

            const valA = a[sortBy] || 0;
            const valB = b[sortBy] || 0;
            return sortDirection === 'asc' ? valA - valB : valB - valA;
        });
    }, [enhancedTokens, searchTerm, selectedChain, selectedCategory, watchlist, sortBy, sortDirection]);
    
    const totalPages = Math.ceil(sortedAndFilteredTokens.length / ITEMS_PER_PAGE);
    const paginatedTokens = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedAndFilteredTokens.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [currentPage, sortedAndFilteredTokens]);


    const handleSort = (key: SortKey) => {
        if (sortBy === key) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortDirection('desc');
        }
    };

    // Stable handlers for memoized children
    const handleOpenTrade = useCallback((token: EnhancedMarketToken) => {
        setTradeToken(token);
        setTradePrice(token.price);
    }, []);

    const handleOpenDetails = useCallback((token: EnhancedMarketToken) => {
        setDetailsToken(token);
    }, []);

    return (
        <div className="min-h-screen bg-[#F9F8F4] flex flex-col w-full overflow-x-hidden">
            {tradeToken && <TradeModal token={tradeToken} currentPrice={tradePrice} isOpen={!!tradeToken} onClose={() => setTradeToken(null)} />}
            {detailsToken && <TokenDetailsModal token={detailsToken} isOpen={!!detailsToken} onClose={() => setDetailsToken(null)} onTrade={() => { setTradeToken(detailsToken); setTradePrice(detailsToken.price); }} price={detailsToken.price} change24h={detailsToken.change24h} />}

            <div className="container mx-auto px-4 md:px-6 py-8 flex-1 w-full">
                
                <div className="flex flex-col lg:flex-row gap-6 justify-between items-start mb-10 border-b border-stone-200 pb-8">
                    <div>
                        <div className="text-xs font-bold text-stone-400 uppercase tracking-widest flex items-center gap-2 mb-1">
                            <Activity size={14} /> Markets / All Assets
                        </div>
                        <h1 className="font-serif text-4xl text-stone-900 leading-tight">Market Terminal</h1>
                    </div>
                    
                    {/* Compact Search & Filter Bar */}
                    <div className="flex flex-row items-center gap-2 w-full lg:w-auto overflow-hidden">
                        <div className="relative min-w-[140px] flex-1 lg:w-64 group bg-white rounded-xl border border-stone-200 shadow-sm focus-within:ring-2 focus-within:ring-nobel-gold/10 focus-within:border-stone-400 transition-all">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                            <input 
                                type="text" 
                                placeholder="Search..." 
                                value={searchTerm} 
                                onChange={(e) => setSearchTerm(e.target.value)} 
                                className="w-full pl-9 pr-8 py-2.5 bg-transparent outline-none text-sm font-medium text-stone-900 placeholder:text-stone-400 truncate" 
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-900 bg-stone-100 rounded-full">
                                    <ArrowRight size={10} />
                                </button>
                            )}
                        </div>

                        {/* Scrollable Filters */}
                        <div className="flex flex-1 overflow-x-auto no-scrollbar gap-2 mask-linear-fade">
                            <div className="flex bg-white p-1 rounded-xl border border-stone-200 shadow-sm shrink-0">
                                {(['ALL', 'SOL', 'SUI'] as const).map((c) => (
                                    <button 
                                        key={c} 
                                        onClick={() => setSelectedChain(c)} 
                                        className={`px-3 py-1.5 rounded-lg text-[10px] md:text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap ${selectedChain === c ? 'bg-stone-900 text-white shadow-md' : 'text-stone-500 hover:bg-stone-50'}`}
                                    >
                                        {c === 'ALL' && <Globe size={12} />}
                                        {c === 'ALL' ? 'All' : c}
                                    </button>
                                ))}
                            </div>

                            <div className="flex bg-white p-1 rounded-xl border border-stone-200 shadow-sm shrink-0">
                                {(['ALL', 'WATCHLIST', 'AI_SIGNALS'] as const).map((cat) => (
                                    <button 
                                        key={cat} 
                                        onClick={() => setSelectedCategory(cat)} 
                                        className={`px-3 py-1.5 rounded-lg text-[10px] md:text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap ${selectedCategory === cat ? 'bg-stone-100 text-stone-900 shadow-inner' : 'text-stone-500 hover:bg-stone-50'}`}
                                    >
                                        {cat === 'WATCHLIST' && <Star size={12} />} 
                                        {cat === 'AI_SIGNALS' && <Brain size={12} />}
                                        {cat === 'ALL' ? 'All' : cat.replace('_', ' ')}
                                    </button>
                                ))}
                            </div>

                            <div className="flex bg-white p-1 rounded-xl border border-stone-200 shadow-sm shrink-0">
                                <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-stone-100 text-stone-900' : 'text-stone-400 hover:bg-stone-50'}`} aria-label="List view"><ListIcon size={16} /></button>
                                <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-stone-100 text-stone-900' : 'text-stone-400 hover:bg-stone-50'}`} aria-label="Grid view"><LayoutGrid size={16} /></button>
                            </div>
                        </div>
                    </div>
                </div>

                <MarketHeader stats={globalStats} />

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32 space-y-4">
                        <div className="w-12 h-12 border-4 border-stone-200 border-t-stone-900 rounded-full animate-spin"></div>
                        <p className="text-stone-400 font-serif italic animate-pulse">Syncing market data...</p>
                    </div>
                ) : (
                    <>
                        {sortedAndFilteredTokens.length === 0 ? (
                            <div className="text-center py-24 bg-white rounded-3xl border border-stone-200 border-dashed">
                                <div className="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center mx-auto mb-4 text-stone-300">
                                    <Search size={24} />
                                </div>
                                <h3 className="text-xl font-serif text-stone-900 mb-2">No markets found</h3>
                                <p className="text-stone-500">Try adjusting your search or filters.</p>
                            </div>
                        ) : viewMode === 'grid' ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-fade-in-up">
                                {paginatedTokens.map((token, idx) => (
                                    <MarketGridCard 
                                        key={token.id} 
                                        token={token} 
                                        idx={idx} 
                                        onDetails={handleOpenDetails} 
                                        onTrade={handleOpenTrade} 
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3 animate-fade-in-up">
                                <div className="grid grid-cols-10 lg:grid-cols-12 gap-4 items-center px-4 py-2 text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">
                                    <div className="col-span-4 lg:col-span-3">Asset</div>
                                    <button onClick={() => handleSort('price')} className="col-span-3 lg:col-span-2 text-right flex items-center justify-end gap-1 hover:text-stone-600">Price {sortBy === 'price' && (sortDirection === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>)}</button>
                                    <button onClick={() => handleSort('change24h')} className="col-span-2 text-right flex items-center justify-end gap-1 hover:text-stone-600">24h % {sortBy === 'change24h' && (sortDirection === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>)}</button>
                                    <button onClick={() => handleSort('smartMoneyCount')} className="hidden lg:flex lg:col-span-2 text-right items-center justify-end gap-1 hover:text-stone-600">Signals {sortBy === 'smartMoneyCount' && (sortDirection === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>)}</button>
                                    <div className="hidden lg:block lg:col-span-2 text-right">Trend</div>
                                    <div className="col-span-1 text-right">Action</div>
                                </div>
                                {paginatedTokens.map((token, idx) => (
                                    <MarketListRow 
                                        key={token.id} 
                                        token={token} 
                                        idx={idx} 
                                        onDetails={handleOpenDetails} 
                                        onTrade={handleOpenTrade} 
                                    />
                                ))}
                            </div>
                        )}
                         {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-4 mt-12 pb-12">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-5 py-2.5 bg-white border border-stone-200 rounded-xl text-sm font-bold text-stone-600 hover:bg-stone-50 disabled:opacity-50 flex items-center gap-2 transition-colors shadow-sm"
                                >
                                    <ChevronLeft size={16} /> Previous
                                </button>
                                <span className="text-sm font-bold text-stone-500 font-mono">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-5 py-2.5 bg-white border border-stone-200 rounded-xl text-sm font-bold text-stone-600 hover:bg-stone-50 disabled:opacity-50 flex items-center gap-2 transition-colors shadow-sm"
                                >
                                    Next <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};